import React, { useState, useId } from "react";
import { MdExpandMore } from "react-icons/md";
import { FaBox } from "react-icons/fa";
import clsx from "clsx";
import { MenuItem } from "../../redux/slices/menuSlice";

interface IngredientsSectionProps {
  product: MenuItem;
}

const IngredientsSection: React.FC<IngredientsSectionProps> = ({ product }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const contentId = useId();

  if (!product.ingredients || product.ingredients.length === 0) return null;

  return (
    <section
      aria-labelledby={`${contentId}-title`}
      className="mb-6 sm:mb-8 rounded-lg border border-gray-200 bg-white overflow-hidden"
    >
      {/* Toggle Button */}
      <button
        type="button"
        aria-expanded={isExpanded}
        aria-controls={contentId}
        onClick={() => setIsExpanded((prev) => !prev)}
        className={clsx(
          "w-full flex items-center justify-between text-left",
          "px-3 sm:px-4 py-2.5 sm:py-3",
          "bg-gray-50 hover:bg-gray-100 transition-colors",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
        )}
      >
        <h2
          id={`${contentId}-title`}
          className="flex items-center gap-2 text-sm sm:text-base md:text-lg font-semibold"
        >
          <FaBox className="h-4 w-4 sm:h-5 sm:w-5 text-gray-700 shrink-0" />
          Ingredients
        </h2>

        <MdExpandMore
          aria-hidden
          className={clsx(
            "h-5 w-5 text-gray-500 transition-transform duration-300 ease-in-out",
            isExpanded && "rotate-180"
          )}
        />
      </button>

      {/* Animated Ingredients List */}
      <div
        id={contentId}
        role="region"
        aria-hidden={!isExpanded}
        className={clsx(
          "flex flex-wrap gap-2 px-3 sm:px-4 transition-[max-height,opacity,padding] duration-300 ease-in-out",
          isExpanded
            ? "max-h-96 py-3 sm:py-4 opacity-100"
            : "max-h-0 py-0 opacity-0 overflow-hidden pointer-events-none"
        )}
      >
        {product.ingredients.map((ingredient, index) => (
          <span
            key={index}
            title={ingredient} // full text on hover
            className={clsx(
              "bg-gray-100 text-gray-800 px-2 sm:px-3 py-1 rounded-full",
              "text-[11px] sm:text-xs md:text-sm truncate max-w-full"
            )}
          >
            {ingredient}
          </span>
        ))}
      </div>
    </section>
  );
};

export default IngredientsSection;
